"use client";

import { useEffect, useRef } from "react";
import { Icon, type IconName } from "@/lib/icons/Icon";
import type { ConfirmDialogOptions, ConfirmVariant } from "@/lib/notifications/confirmDialog";
import styles from "./ConfirmDialog.module.css";

/**
 * ConfirmDialog — modal blocking dialog z ikoną wariantu + 2 przyciski.
 *
 * Renderowany przez `<ConfirmDialogProvider />` gdy `confirmDialog()` zostanie
 * wywołane. Sam komponent jest "dumb" — dostaje opts + callbacks, robi UI.
 *
 * **Native HTML5 `<dialog>` element** — daje za darmo:
 *   - Focus trap (Tab nie wychodzi poza dialog)
 *   - Escape key zamyka (zachowanie default modal)
 *   - `::backdrop` pseudo-element dla overlay
 *   - `role="dialog"` + `aria-modal="true"` (a11y)
 *   - Inertness reszty strony (modal blokuje interakcję)
 *
 * **Wariant:** `info` (niebieski), `warning` (amber), `danger` (czerwony).
 * Każdy wariant zmienia ikonę + kolor accent (border ikony, primary button bg).
 */
export function ConfirmDialog({ opts, onConfirm, onCancel }: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const variant: ConfirmVariant = opts.variant ?? "warning";

  /* Mount -> showModal() (native API otwiera dialog z backdrop + focus trap). */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
  }, []);

  /* Native `<dialog>` emit `cancel` event na Escape — łapiemy i traktujemy jak Anuluj. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onDialogCancel = (e: Event) => {
      e.preventDefault(); //prevent default close, manual handling przez onCancel
      onCancel();
    };
    dialog.addEventListener("cancel", onDialogCancel);
    return () => dialog.removeEventListener("cancel", onDialogCancel);
  }, [onCancel]);

  /* Klik na backdrop (poza dialog content) -> cancel.
     Native `<dialog>::backdrop` clicks bubble do dialog elementa, sprawdzamy
     przez target === currentTarget (czyli klik na sam dialog, nie na content). */
  const onBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === dialogRef.current) {
      onCancel();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${styles[`variant_${variant}`]}`}
      onClick={onBackdropClick}
      aria-labelledby="confirmDialogTitle"
      aria-describedby="confirmDialogMessage"
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <span className={styles.iconWrap} aria-hidden="true">
            <Icon name={ICON_PER_VARIANT[variant]} className={styles.icon} />
          </span>
          <h2 id="confirmDialogTitle" className={styles.title}>
            {opts.title}
          </h2>
        </header>

        <p id="confirmDialogMessage" className={styles.message}>
          {opts.message}
        </p>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={onCancel}
            autoFocus
          >
            {opts.cancelLabel ?? "Anuluj"}
          </button>
          <button
            type="button"
            className={`${styles.btnConfirm} ${styles[`btnConfirm_${variant}`]}`}
            onClick={onConfirm}
          >
            {opts.confirmLabel ?? "Potwierdź"}
          </button>
        </footer>
      </div>
    </dialog>
  );
}

const ICON_PER_VARIANT: Record<ConfirmVariant, IconName> = {
  info: "info",
  warning: "warning-circle",
  danger: "x-circle",
};

export type ConfirmDialogProps = {
  opts: ConfirmDialogOptions;
  onConfirm: () => void;
  onCancel: () => void;
};
